import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import Sheet from './Sheet';
import { API_BASE, SOCKET_URL, SOCKET_PATH, formatCurrency } from '../config/api';
import { isFullyPaid, isPartial, amountDueOf, paidAmountOf } from '../billPayment';
import './SepayModal.css';

const HOLD_SECONDS = 3600; // đơn được giữ chỗ 60 phút

export default function SepayModal({ show, onClose, bill, onPaymentSuccess }) {
    const [qrData, setQrData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(null);
    const [paid, setPaid] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [countdown, setCountdown] = useState(HOLD_SECONDS);
    const pollRef = useRef(null);

    const orderCode = bill?.orderCode || `TT${bill?.id || ''}`;
    // After a flavour change on a paid order only the difference is owed — the QR asks for that.
    const due = amountDueOf(bill);
    const amount = due > 0 ? due : (Number(bill?.totalAmount) || 0);
    const payingDifference = isPartial(bill);

    const [checking, setChecking] = useState(false);
    const [qrFile, setQrFile] = useState(null);
    const [qrBlobUrl, setQrBlobUrl] = useState(null);
    const qrFileRef = useRef(null);
    const [showSaveHelp, setShowSaveHelp] = useState(false);

    useEffect(() => {
        if (!show || !bill) return;

        setPaid(isFullyPaid(bill));
        setCountdown(HOLD_SECONDS);
        setError(null);
        setLoading(true);

        (async () => {
            try {
                const res = await axios.post(`${API_BASE}/payment/generate-qr`, {
                    orderCode,
                    totalAmount: amount,
                    billId: bill.id,
                });
                if (res.data?.qrUrl) setQrData(res.data);
                else setError('Không thể tạo mã QR thanh toán.');
            } catch (err) {
                console.error('Lỗi tạo QR SePay:', err);
                setError('Có lỗi khi tạo mã VietQR. Bạn vẫn có thể chuyển khoản thủ công theo thông tin bên dưới.');
            } finally {
                setLoading(false);
            }
        })();

        // Real-time payment notification via Socket.IO
        const socket = io(SOCKET_URL, {
            path: SOCKET_PATH,
            transports: ['websocket', 'polling'],
            withCredentials: true,
        });

        const markPaid = () => {
            setPaid(true);
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            if (onPaymentSuccess) onPaymentSuccess(bill);
        };

        socket.on('paymentSuccess', (data) => {
            // Only settle on an event that names this order — a broadcast with no
            // payload used to mark whatever order happened to be open as paid.
            if (data && (data.orderCode === orderCode || parseInt(data.id || data.billId, 10) === parseInt(bill.id, 10))) {
                markPaid();
            }
        });

        socket.on('updateStatusBill', (data) => {
            if (data && (data.orderCode === orderCode || parseInt(data.id, 10) === parseInt(bill.id, 10))) {
                // An order still owing a difference can read "Đã thanh toán"-adjacent statuses;
                // paymentStatus is the authority whenever the event carries it.
                const settled = data.paymentStatus ? data.paymentStatus === 'paid' : data.status === 'Đã thanh toán';
                if (settled) markPaid();
            }
        });

        const checkStatus = async () => {
            try {
                const res = await axios.get(`${API_BASE}/payment/check-status/${orderCode}`);
                if (res.data?.isPaid) {
                    markPaid();
                    if (pollRef.current) clearInterval(pollRef.current);
                    return true;
                }
            } catch (e) { /* keep polling */ }
            return false;
        };

        // Check immediately once modal opens
        checkStatus();

        // Fast fallback polling every 1.2s instead of 3s
        pollRef.current = setInterval(checkStatus, 1200);

        return () => {
            socket.disconnect();
            if (pollRef.current) clearInterval(pollRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, bill?.id, orderCode]);

    useEffect(() => {
        if (!show || paid) return;
        const timer = setInterval(() => {
            setCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [show, paid]);

    const bank = qrData?.bankShortName || 'MBBank';
    const account = qrData?.accountNumber || '0398176703';
    const holder = qrData?.accountHolder || 'VU THE HAI';
    const qrImage = qrData?.qrUrl
        || `https://qr.sepay.vn/img?acc=${account}&bank=${bank}&amount=${amount}&des=${orderCode}`;

    // Preload QR file & blob URL so navigator.share() can run SYNCHRONOUSLY on user tap
    useEffect(() => {
        if (!show || !qrImage) return;
        let active = true;
        (async () => {
            try {
                let blob = null;
                try {
                    const r = await fetch(qrImage, { mode: 'cors' });
                    if (r.ok) blob = await r.blob();
                } catch (e) { /* fallback to proxy */ }

                if (!blob) {
                    const proxyUrl = `${API_BASE}/payment/download-qr?url=${encodeURIComponent(qrImage)}&code=${encodeURIComponent(orderCode)}`;
                    const r = await fetch(proxyUrl);
                    if (r.ok) blob = await r.blob();
                }

                if (blob && active) {
                    const file = new File([blob], `VietQR-${orderCode}.png`, { type: 'image/png' });
                    qrFileRef.current = file;
                    setQrFile(file);
                    const objUrl = URL.createObjectURL(blob);
                    setQrBlobUrl(objUrl);
                }
            } catch (err) {
                console.warn('Lỗi chuẩn bị ảnh QR:', err);
            }
        })();
        return () => { active = false; };
    }, [show, qrImage, orderCode]);

    const copy = (text, field) => {
        if (!text) return;
        const value = String(text);
        const done = () => {
            setCopied(field);
            setTimeout(() => setCopied(null), 1800);
        };
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(value).then(done).catch(done);
        } else {
            const el = document.createElement('textarea');
            el.value = value;
            document.body.appendChild(el);
            el.select();
            try { document.execCommand('copy'); } catch (e) { /* ignore */ }
            document.body.removeChild(el);
            done();
        }
    };

    const copyAll = () => {
        const text = `NGÂN HÀNG: ${bank}\nSTK: ${account}\nCHỦ TÀI KHOẢN: ${holder}\nSỐ TIỀN: ${amount}đ\nNỘI DUNG: ${orderCode}`;
        copy(text, 'all');
    };

    /**
     * Save the VietQR image directly to the device's photo gallery / files.
     */
    const downloadQr = async () => {
        if (downloading) return;
        setDownloading(true);
        const fileName = `VietQR-${orderCode}.png`;
        const proxyUrl = `${API_BASE}/payment/download-qr?url=${encodeURIComponent(qrImage)}&code=${encodeURIComponent(orderCode)}`;
        const targetUrl = qrBlobUrl || proxyUrl;
        const file = qrFileRef.current || qrFile;

        // 1. Mobile (iOS Safari / Android):
        // Calling navigator.share SYNCHRONOUSLY without any preceding 'await fetch' preserves the user gesture!
        // On iOS, this opens the native iOS Share Sheet with "Lưu hình ảnh" (Save Image) which writes directly to the Photos app.
        if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
                await navigator.share({
                    files: [file],
                    title: `Mã VietQR đơn #${orderCode}`,
                    text: `Mã VietQR thanh toán đơn hàng #${orderCode}`,
                });
                setCopied('downloaded');
                setTimeout(() => setCopied(null), 2500);
                setDownloading(false);
                return;
            } catch (shareErr) {
                if (shareErr.name === 'AbortError') {
                    // User dismissed the share sheet
                    setDownloading(false);
                    return;
                }
                console.warn('Share API failed, falling back:', shareErr);
            }
        }

        // 2. Desktop and standard browsers: trigger immediate file download
        try {
            const a = document.createElement('a');
            a.href = targetUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setCopied('downloaded');
            setTimeout(() => setCopied(null), 2500);
        } catch (err) {
            console.warn('Lỗi tải QR:', err);
        } finally {
            setDownloading(false);
        }

        // 3. For mobile devices where user might not find the download in Photos:
        // Also show the clear Save-to-Photos guide overlay
        setShowSaveHelp(true);
    };

    const mm = String(Math.floor(countdown / 60)).padStart(2, '0');
    const ss = String(countdown % 60).padStart(2, '0');

    return (
        <>
            <Sheet
                open={!!show}
                onClose={onClose}
                subtitle={paid ? 'Hoàn tất' : `Đơn #${orderCode}`}
                title={paid ? 'Thanh toán thành công' : payingDifference ? 'Thanh toán phần chênh lệch' : 'Thanh toán VietQR'}
                footer={paid ? (
                    <button
                        type="button"
                        className="btn btn-primary btn-block"
                        onClick={() => { if (onPaymentSuccess) onPaymentSuccess(bill); onClose(); }}
                    >
                        Xem trạng thái đơn hàng
                    </button>
                ) : (
                    <div className="pay-foot">
                        <i className="fa-solid fa-shield-halved" />
                        <span>Tự động xác nhận trong 3–10 giây sau khi chuyển khoản</span>
                    </div>
                )}
            >
                {paid ? (
                    <div className="pay-done">
                        <div className="pay-done-icon"><i className="fa-solid fa-check" /></div>
                        <h3>Đã nhận được thanh toán!</h3>
                        <p>
                            Đơn <strong>#{orderCode}</strong> đã được ghi nhận. Bánh sẽ được làm và giao đúng lịch hẹn của bạn.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="pay-status">
                            <span className="pay-live"><span className="dot" /> Đang chờ chuyển khoản</span>
                            {countdown > 0 ? (
                                <span className="pay-timer">Còn <strong>{mm}:{ss}</strong></span>
                            ) : (
                                <span className="pay-timer">Đơn vẫn được giữ — thanh toán bất kỳ lúc nào</span>
                            )}
                        </div>

                        <div className="qr-card">
                            {loading ? (
                                <div className="qr-loading">
                                    <i className="fa-solid fa-spinner spin" />
                                    <span>Đang tạo mã VietQR...</span>
                                </div>
                            ) : (
                                <>
                                    {error && <div className="qr-error">{error}</div>}
                                    <div
                                        className="qr-frame"
                                        onClick={downloadQr}
                                        title="Chạm giữ 1 giây để lưu ảnh vào Thư viện"
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <img
                                            src={qrBlobUrl || qrImage}
                                            alt={`VietQR đơn ${orderCode}`}
                                            style={{ WebkitTouchCallout: 'default', userSelect: 'auto', touchAction: 'manipulation' }}
                                        />
                                        <div className="qr-frame-badge">
                                            <i className="fa-solid fa-hand-pointer" /> Chạm giữ 1s để Lưu vào Ảnh
                                        </div>
                                    </div>
                                    <div className="qr-amount">{formatCurrency(amount)}</div>
                                    {payingDifference && (
                                        <div className="qr-amount-note">
                                            Đơn đã đổi vị · tổng mới {formatCurrency(bill.totalAmount)} · đã trả {formatCurrency(paidAmountOf(bill))}
                                        </div>
                                    )}
                                    <div className="qr-hint">Mở app ngân hàng · quét mã · chuyển khoản</div>

                                    <div className="qr-save-guide-box">
                                        <i className="fa-solid fa-camera" />
                                        <div className="qr-save-guide-text">
                                            <strong>Lưu vào Thư viện ảnh:</strong> Bấm <strong>"Lưu ảnh QR"</strong> ➔ chọn <em>"Lưu hình ảnh"</em> (hoặc <strong>chạm giữ 1s vào ảnh QR</strong>). Sau đó mở App ngân hàng quét ảnh trong Thư viện.
                                        </div>
                                    </div>

                                    <div className="qr-actions">
                                        <button
                                            type="button"
                                            className="qr-btn primary"
                                            onClick={downloadQr}
                                            disabled={downloading}
                                        >
                                            <i className={downloading ? 'fa-solid fa-spinner spin' : (copied === 'downloaded' ? 'fa-solid fa-circle-check' : 'fa-solid fa-download')} />
                                            {downloading ? 'Đang lưu...' : (copied === 'downloaded' ? 'Đã lưu / Chia sẻ' : 'Lưu ảnh QR')}
                                        </button>
                                        <button type="button" className="qr-btn" onClick={() => copy(orderCode, 'qrcode')}>
                                            <i className={copied === 'qrcode' ? 'fa-solid fa-check' : 'fa-regular fa-copy'} />
                                            {copied === 'qrcode' ? 'Đã chép mã' : 'Chép nội dung'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="bank-card">
                            <div className="bank-row">
                                <span>Ngân hàng</span>
                                <strong>{bank}</strong>
                            </div>

                            <div className="bank-row">
                                <span>Số tài khoản</span>
                                <span>
                                    <strong>{account}</strong>
                                    <button type="button" className="copy-btn" onClick={() => copy(account, 'acc')} aria-label="Sao chép số tài khoản">
                                        <i className={copied === 'acc' ? 'fa-solid fa-check' : 'fa-regular fa-copy'} />
                                    </button>
                                </span>
                            </div>

                            <div className="bank-row">
                                <span>Chủ tài khoản</span>
                                <strong>{holder}</strong>
                            </div>

                            <div className="bank-row accent">
                                <span>Nội dung CK</span>
                                <span>
                                    <strong className="code">{orderCode}</strong>
                                    <button type="button" className="copy-btn on" onClick={() => copy(orderCode, 'code')} aria-label="Sao chép nội dung chuyển khoản">
                                        <i className={copied === 'code' ? 'fa-solid fa-check' : 'fa-regular fa-copy'} />
                                    </button>
                                </span>
                            </div>

                            <div className="bank-warn">
                                <i className="fa-solid fa-circle-exclamation" />
                                Giữ đúng nội dung <strong>{orderCode}</strong> để hệ thống tự khớp đơn.
                            </div>
                        </div>

                        <div style={{ marginTop: 8 }}>
                            <button
                                type="button"
                                className="btn btn-outline btn-block"
                                style={{ minHeight: 38, fontSize: 12.5 }}
                                onClick={copyAll}
                            >
                                <i className={copied === 'all' ? 'fa-solid fa-check' : 'fa-regular fa-copy'} />
                                {copied === 'all' ? 'Đã sao chép tất cả thông tin CK' : 'Sao chép toàn bộ thông tin CK'}
                            </button>
                        </div>

                        <button
                            type="button"
                            className="btn btn-soft btn-block"
                            style={{ marginTop: 14, minHeight: 44 }}
                            disabled={checking || paid}
                            onClick={async () => {
                                setChecking(true);
                                try {
                                    const res = await axios.get(`${API_BASE}/payment/check-status/${orderCode}`);
                                    if (res.data?.isPaid) {
                                        setPaid(true);
                                        if (onPaymentSuccess) onPaymentSuccess(bill);
                                    } else {
                                        setCopied('checking_pending');
                                        setTimeout(() => setCopied(null), 3000);
                                    }
                                } catch (err) {
                                    setCopied('checking_pending');
                                    setTimeout(() => setCopied(null), 3000);
                                } finally {
                                    setChecking(false);
                                }
                            }}
                        >
                            {checking ? (
                                <><i className="fa-solid fa-spinner spin" /> Đang kiểm tra giao dịch...</>
                            ) : (
                                <><i className="fa-solid fa-rotate" /> Tôi đã chuyển khoản · Kiểm tra ngay</>
                            )}
                        </button>

                        {copied === 'checking_pending' && (
                            <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                                Ngân hàng đang xử lý giao dịch. Hệ thống đang tự động kiểm tra mỗi giây...
                            </div>
                        )}
                    </>
                )}
            </Sheet>

            {showSaveHelp && (
                <div className="save-help-overlay" onClick={() => setShowSaveHelp(false)}>
                    <div className="save-help-card" onClick={(e) => e.stopPropagation()}>
                        <div className="save-help-head">
                            <div className="save-help-head-title">
                                <i className="fa-solid fa-image" />
                                <span>Lưu QR vào Thư viện ảnh</span>
                            </div>
                            <button type="button" className="icon-btn" onClick={() => setShowSaveHelp(false)} aria-label="Đóng">
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>

                        <div className="save-help-img-box">
                            <img
                                src={qrBlobUrl || qrImage}
                                alt={`VietQR ${orderCode}`}
                                style={{ WebkitTouchCallout: 'default', userSelect: 'auto' }}
                            />
                            <div className="save-help-img-tip">
                                👆 Chạm và giữ tay vào ảnh trên 1 giây
                            </div>
                        </div>

                        <div className="save-help-steps">
                            <div className="save-help-step">
                                <span className="step-num">1</span>
                                <div><strong>Chạm & giữ 1 giây</strong> vào ảnh mã QR ở trên</div>
                            </div>
                            <div className="save-help-step">
                                <span className="step-num">2</span>
                                <div>Bấm chọn <strong>"Lưu vào Ảnh"</strong> (Save Image)</div>
                            </div>
                            <div className="save-help-step">
                                <span className="step-num">3</span>
                                <div>Mở App Ngân hàng ➔ Chọn <strong>Quét QR</strong> ➔ Chọn ảnh từ Thư viện</div>
                            </div>
                        </div>

                        <div className="save-help-actions">
                            <button type="button" className="btn btn-primary btn-block" onClick={() => setShowSaveHelp(false)}>
                                Đã hiểu · Quay lại thanh toán
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
